import { useRequiredContext } from "@redotech/react-util/context";
import { disputeResolveUiFlag } from "@redotech/redo-dispute-settings-common/dispute-feature-flags";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useQuery } from "@tanstack/react-query";

export interface DisputeResolveEnabledState {
  readonly pending: boolean;
  readonly enabled: boolean;
}

/**
 * Feature flag for the Disputely RESOLVE ("deflection") tab (the finance-nav
 * "RESOLVE" sub-item under Reclaim). Reads the DISPUTE_RESOLVE_UI LaunchDarkly
 * flag scoped to the current team. Defaults to `false` while `pending` and when
 * LD is unreachable, so the tab stays hidden until the flag resolves on. Mirrors
 * useDisputeAlertsEnrollmentEnabled.
 */
export function useDisputeResolveEnabled(): DisputeResolveEnabledState {
  const platformRpcClient = useRequiredContext(
    RedoPlatformAuthRpcClientContext,
  );
  const team = useRequiredContext(TeamContext);
  const teamId = String(team._id);

  const query = useQuery({
    queryKey: ["featureFlag", disputeResolveUiFlag.key, teamId],
    queryFn: async () => {
      const response = await platformRpcClient.getFeatureFlag({
        flagKey: disputeResolveUiFlag.key,
        defaultValue: disputeResolveUiFlag.defaultValue,
      });
      return response.enabled;
    },
    staleTime: Infinity,
  });

  return { pending: query.isPending, enabled: query.data ?? false };
}
